import React, { useState } from 'react';
import styled from 'styled-components';
import { formatDistanceToNow } from 'date-fns';
import useTranslation from 'next-translate/useTranslation';
import {
  style,
  User,
  Comment as CommentType,
  useCommentMusicKnowledgeHub,
  useAuthentication,
} from '../../../api';
import { ProfileLinkStyled } from '../../common/Link';
import { mobile, tablet, desktop, noDesktop } from '../../../style/constants';
import UserImage from '../../common/UserImage';
import { TextBold12, TextRegular14, TextRegular12 } from '../../common/Text';
import { HeartBorder, Heart } from '../../common/Icons';
import { Button } from '../../../VuexyDesign/atoms/_index';
import CommentsReplyForm from './CommentsReplyForm';
import AuthenticationPopupDialog from '../../Modals/AuthenticationPopupDialog';

const CommentsForPost = ({
  user,
  id,
  postId,
  userIsLoggedIn = false,
  withReply = userIsLoggedIn,
  userId,
  deleteComment,
  parentCommentId,
}: {
  user: User;
  id: number;
  replies: CommentType[];
  postId: number;
  userIsLoggedIn?: boolean;
  withReply?: boolean;
  userId?: number;
  deleteComment: (
    commentId: number,
    parent: {
      parentCommentId?: number;
      postId?: number;
    },
  ) => void;
  parentCommentId?: number;
}) => {
  const { t } = useTranslation('tracks');
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [isAuthenticationDialogOpen, setIsAuthenticationDialogOpen] = useState(
    false,
  );
  const { isLoggedIn } = useAuthentication();
  const {
    data: comment,
    like,
    unlike,
    deleteCommentData,
  } = useCommentMusicKnowledgeHub(id);

  if (!comment) {
    return null;
  }

  const {
    body: message,
    logged_in_user_liked,
    like_count: likes,
    created_at,
    replies,
  } = comment;

  const deleteCommentHandler = () => {
    if (parentCommentId) {
      deleteCommentData({ commentIdData: id, parentCommentId });
    } else {
      deleteComment(id, { parentCommentId, postId });
    }
  };
  const handleAuthenticationDialogClose = () => {
    setIsAuthenticationDialogOpen(false);
  };
  const handleAuthenticationDialogOpen = () => {
    setIsAuthenticationDialogOpen(true);
  };

  return (
    <Outer>
      <Container>
        <ImgBox>
          <ProfileLinkStyled profileId={user.id}>
            <UserImage src={user.profile_photo} size={22} />
          </ProfileLinkStyled>
          <ProfileLinkStyled profileId={user.id}>
            <TextBold12 style={{ marginLeft: 12 }}>
              {user.display_name}
            </TextBold12>
          </ProfileLinkStyled>
          <TextRegular12
            style={{
              marginLeft: 4,
              color: style.grey_dark_new,
            }}
          >
            {formatDistanceToNow(new Date(created_at * 1000))} ago
          </TextRegular12>
          <Likes
            likes={likes}
            onClick={() => {
              if (isLoggedIn) {
                if (logged_in_user_liked) {
                  unlike();
                } else {
                  like();
                }
              } else {
                handleAuthenticationDialogOpen();
              }
            }}
            liked={!!logged_in_user_liked}
          />
        </ImgBox>
        <Col>
          <CommentContent>
            <CommentData>
              <MessageWrapper>
                <Message>{message}</Message>
                <ReplyDeleteWrapper>
                  {withReply && (
                    <>
                      <ReplyButton
                        outline
                        color="dark"
                        onClick={() => setShowReplyForm(!showReplyForm)}
                        className="comment-reply"
                      >
                        {t('tracks:reply') as string}
                      </ReplyButton>
                    </>
                  )}
                  {userId === user.id && (
                    <>
                      <Delete
                        outline
                        size="sm"
                        className="text-dark"
                        onClick={() => deleteCommentHandler()}
                        text={t('tracks:delete') as string}
                      />
                    </>
                  )}
                </ReplyDeleteWrapper>
              </MessageWrapper>
              {withReply && showReplyForm && (
                <CommentsReplyForm
                  postId={postId}
                  commentId={id}
                  hide={() => setShowReplyForm(false)}
                />
              )}
            </CommentData>
          </CommentContent>
        </Col>
      </Container>
      {!!replies.length && (
        <RepliesContainer>
          {replies.map((reply) => (
            <CommentsForPost
              {...reply}
              key={reply.id}
              withReply={false}
              userId={userId}
              postId={postId}
              deleteComment={deleteComment}
              parentCommentId={id}
            />
          ))}
        </RepliesContainer>
      )}

      <AuthenticationPopupDialog
        isOpen={isAuthenticationDialogOpen}
        onClose={handleAuthenticationDialogClose}
      />
    </Outer>
  );
};

const MessageWrapper = styled.div`
  display: flex;
`;

const Likes = styled(
  ({
    likes,
    onClick,
    className,
    liked = false,
  }: {
    likes: number;
    onClick?: () => void;
    className?: string;
    liked?: boolean;
  }) => (
    <LikesWrapper onClick={onClick} className={className}>
      {likes > 0 && <TextRegular14>{likes}</TextRegular14>}
      {liked ? (
        <Heart size={14} color={style.primary_pink_new} />
      ) : (
        <HeartBorder size={14} />
      )}
    </LikesWrapper>
  ),
)``;

const LikesWrapper = styled.div`
  display: flex;
  align-items: center;

  cursor: pointer;

  svg {
    margin-left: 4px;
  }
`;

const ReplyButton = styled(Button)`
  font-size: 11px;
  white-space: nowrap;
  min-height: 20px !important;
  padding: 3px 12px !important;
  font-weight: 700;
`;

const Outer = styled.div`
  word-break: break-word;
  padding-right: 10px;
`;

const RepliesContainer = styled.div`
  padding-top: 10px;
  @media ${tablet} {
    padding-left: 50px;
  }
  @media ${mobile} {
    padding-left: 25px;
  }
`;

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;

  border-bottom: 1px solid #e5e2e2;
  padding-top: 10px;
  padding-bottom: 12px;
`;

const CommentContent = styled.div`
  display: flex;
  flex-direction: row;
  font-size: 12px;
`;

const CommentData = styled.div`
  word-break: break-word;
  width: 100%;
`;

const Delete = styled(Button)`
  font-size: 11px;
  white-space: nowrap;
  min-height: 20px !important;
  padding: 3px 12px !important;
  font-weight: 700;
  border: 1.5px solid #d8d8d8 !important;
`;

const ImgBox = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  margin-bottom: 8px;

  ${Likes} {
    margin-left: auto;
  }
`;

const Col = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const Message = styled(TextRegular14)`
  color: ${style.black};
  word-wrap: break-word;
  margin-left: 2.5rem;
  @media ${desktop} {
    flex-basis: 90%;
  }

  @media ${noDesktop} {
    flex-basis: 80%;
  }
`;

const ReplyDeleteWrapper = styled.div`
  display: flex;
  height: 20px;
  gap: 5px;

  @media ${desktop} {
    align-items: flex-end;
  }

  @media ${noDesktop} {
    justify-content: flex-end;
    svg {
      margin-left: 4px;
    }
  }
  margin-left: auto;
`;

export default CommentsForPost;
